import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { ReactTable } from "components";
import { useShowPriceInVat } from "hooks";
import { DMS_CAPABILITIES, INTERVENTION_ELEMENT_STATUSES } from "models";
import { InterventionElementIcon } from "modules/AppointmentDetails/components/Interventions/components/Subitems/components/InterventionElementIcon";
import "modules/AppointmentDetails/components/Interventions/components/Subitems/components/SubitemsTable/SubitemsTable.scss";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { enumToDropdownOptions, getPriceInVAT } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { DealerLocation } from "models";
import type { InterventionElement } from "models/InterventionElement";

type SubitemsTableProps = {
  elements?: InterventionElement[];
  location?: DealerLocation | null;
};

const columnHelper = createColumnHelper<InterventionElement>();

const SubitemsTable = ({ elements, location }: SubitemsTableProps) => {
  const { t } = useTranslation();

  const showPriceInVat = useShowPriceInVat();
  const selectedLocation = useAppointmentLocation();

  const interventionElementStatuses = enumToDropdownOptions({ enumObj: INTERVENTION_ELEMENT_STATUSES, t });

  let columns = [
    columnHelper.accessor(row => row, {
      id: "element_type",
      header: t("v8_type", "Type"),
      size: 20,
      cell: ({ row: { original } }) => <InterventionElementIcon elementType={original.element_type} parentDmsNr={original.parent_dms_nr} />
    }),
    columnHelper.accessor(row => row, {
      id: "id",
      cell: ({ row: { original } }) => `${original.brand_code} ${original.reference_code}`.trim(),
      size: 40,
      header: () => "#"
    }),
    columnHelper.accessor(row => row, {
      id: "description",
      header: () => t("v8_description", "Description"),
      size: 520,
      cell: ({ row: { original } }) => original.description
    }),
    columnHelper.accessor(row => row, {
      id: "unit",
      header: () => <span className="ellipsis">{t("v8_unit", "Unit")} €</span>,
      size: 60,
      cell: ({
        row: {
          original: { price, vat }
        }
      }) => {
        const formattedPriceValue = Number(price || 0);
        const formattedPrice = showPriceInVat ? formatPrice(getPriceInVAT(formattedPriceValue, selectedLocation, vat)) : formatPrice(formattedPriceValue);
        return formattedPrice;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "quantity",
      header: () => <span className="ellipsis">{t("v8_quantity", "Quantity")}</span>,
      size: 40,
      cell: ({ row: { original } }) => original.quantity
    }),
    columnHelper.accessor(row => row, {
      id: "price",
      header: () => <span className="ellipsis">{t("v8_price", "Price")} €</span>,
      size: 60,
      cell: ({
        row: {
          original: { price, quantity, vat }
        }
      }) => {
        const total = Number(price || 0) * Number(quantity || 1);
        return showPriceInVat ? formatPrice(getPriceInVAT(total, selectedLocation, vat)) : formatPrice(total);
      }
    })
  ];

  const statusColumn = columnHelper.accessor(row => row, {
    id: "status",
    header: () => <span className="ellipsis">{t("v8_status", "Status")}</span>,
    size: 60,
    cell: ({ row: { original } }) => (original.status ? interventionElementStatuses.find(({ key }) => key === original.status)?.text || "" : "")
  });

  if (location?.dms_capability_ids?.includes(DMS_CAPABILITIES.InterventionElementPartStatus)) columns = [...columns, statusColumn];

  return (
    <div className="SubitemsTable">
      <ReactTable columns={columns} data={elements} />
    </div>
  );
};
export default SubitemsTable;
